import React, { useState } from 'react';
import { Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useT } from '../lib/i18n';
import { contactAPI } from '../lib/api';
import CaptchaWidget from './CaptchaWidget';

// ─────────────────────────────────────────────────────────────────────────
// ModernContact — the «Moderno» theme "Let's Work Together" (s139, approved by
// Anthony: C6 "Pull Quote + Form"). Left: eyebrow (title · subtitle), the first
// line of the description as a large serif quote with an orange mark, the rest
// of the description below (its own formatting). Right: the same form as
// before (name / email / message + captcha → CMS Contacts) with line fields.
// Texts/placeholders from CMS → Contact Section. Colours from theme tokens.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const clean = (html) => String(html || '').replace(/&nbsp;?/gi, ' ').replace(/ /g, ' ');
const plain = (html) => clean(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

// First block of the description becomes the quote when it is a short line.
function splitDescription(html) {
  const blocks = clean(html).match(/<(p|h[1-6])[^>]*>[\s\S]*?<\/\1>/gi) || [];
  const first = blocks.find(b => plain(b));
  if (!first || plain(first).length > 140) return { quote: '', rest: clean(html) };
  return { quote: plain(first), rest: clean(html).replace(first, '') };
}

export default function ModernContact({ contactSettings }) {
  const tt = useT();
  const cs = contactSettings || {};
  const title = tt(cs.title) || 'Contact';
  const subtitle = tt(cs.subtitle) || "Let's Work Together";
  const { quote, rest } = splitDescription(tt(cs.description));
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [captchaToken, setCaptchaToken] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await contactAPI.submit({ ...form, captcha_token: captchaToken });
      toast.success('Message sent!');
      setForm({ name: '', email: '', message: '' });
      setCaptchaToken('');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  const field = 'w-full bg-transparent border-0 border-b-[1.5px] px-0 py-3.5 text-base focus:outline-none focus:ring-0 transition-colors duration-200';
  const fieldStyle = { borderColor: 'rgba(20,26,58,.2)', color: 'var(--color-heading, #1a2332)' };
  const focusOn = (e) => { e.target.style.borderColor = 'var(--color-accent, #ff9c1b)'; };
  const focusOff = (e) => { e.target.style.borderColor = 'rgba(20,26,58,.2)'; };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] py-20 lg:py-28 grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-12 lg:gap-20 items-start">
        <div className="flex flex-col gap-6">
          <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: 'var(--accent-strong, #b86500)' }} data-testid="contact-title">
            <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{title} · {subtitle}
          </div>
          <p className="m-0" style={{ fontFamily: SERIF, fontStyle: 'italic', fontWeight: 500, fontSize: 'clamp(2rem, 3.8vw, 3.4rem)', lineHeight: 1.12, color: 'var(--color-heading, #1a2332)', textWrap: 'balance' }} data-testid="contact-quote">
            <span style={{ color: 'var(--color-accent, #ff9c1b)' }}>“</span>{quote || subtitle}
          </p>
          {plain(rest) && (
            <div className="rich-text-content text-base leading-relaxed max-w-xl [&_p]:mb-3 [&_p:empty]:hidden" style={{ color: 'var(--color-body-text, #475569)' }} dangerouslySetInnerHTML={{ __html: rest }} data-testid="contact-description" />
          )}
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-2 lg:pt-10" data-testid="contact-form">
          <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} onFocus={focusOn} onBlur={focusOff} required
            placeholder={tt(cs.name_placeholder) || 'Your name'} aria-label="Your name" className={field} style={fieldStyle} data-testid="contact-name" />
          <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} onFocus={focusOn} onBlur={focusOff} required
            placeholder={tt(cs.email_placeholder) || 'Email address'} aria-label="Email address" className={field} style={fieldStyle} data-testid="contact-email" />
          <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} onFocus={focusOn} onBlur={focusOff} required rows={4}
            placeholder={tt(cs.message_placeholder) || 'How can we help?'} aria-label="Message" className={`${field} resize-none`} style={fieldStyle} data-testid="contact-message" />
          <div className="pt-4"><CaptchaWidget onChange={setCaptchaToken} testId="contact-captcha-modern" /></div>
          <button type="submit" disabled={sending} className="self-start mt-4 inline-flex items-center gap-2 font-semibold text-sm px-7 py-4 rounded-[2px] mx-btn mx-btn--primary disabled:opacity-50"
            style={{ background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #0f172a)' }} data-testid="contact-submit">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{tt(cs.submit_text) || 'Send message'} {!sending && <ArrowRight className="w-4 h-4" />}
          </button>
        </form>
      </div>
    </section>
  );
}
